<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-stethoscope"></i> {{ $t('cdTitle') }}</h1>
        <p class="mb-0 text-muted">{{ $t('cdSubtitle') }}</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item">{{ $t('diagBreadcrumb') }}</li>
        <li class="breadcrumb-item">{{ $t('cdTitle') }}</li>
      </ul>
    </div>

    <!-- Thanh tìm -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="form-row">
          <!-- Tìm theo Call ID -->
          <div class="col-lg-5 mb-2">
            <label class="small text-muted mb-1">{{ $t('cdCallId') }}</label>
            <b-input-group>
              <b-form-input v-model.trim="callIdInput" :placeholder="$t('cdCallIdPh')" @keyup.enter="analyzeById" />
              <b-input-group-append>
                <b-button variant="primary" :disabled="!callIdInput || analyzing" @click="analyzeById">
                  <b-spinner small v-if="analyzing && byId" /> {{ $t('cdDiagnose') }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
          <!-- Phân cách -->
          <div class="col-lg-auto d-none d-lg-flex align-items-end pb-2">
            <span class="text-muted small">{{ $t('cdOr') }}</span>
          </div>
          <!-- Tìm theo số + thời gian -->
          <div class="col-lg mb-2">
            <label class="small text-muted mb-1">{{ $t('cdByNumber') }}</label>
            <div class="form-row">
              <div class="col-sm-4 mb-1">
                <b-form-input v-model.trim="keyword" :placeholder="$t('cdNumberPh')" @keyup.enter="searchByNumber" />
              </div>
              <div class="col-sm-3 mb-1"><b-form-input type="date" v-model="fromDate" /></div>
              <div class="col-sm-3 mb-1"><b-form-input type="date" v-model="toDate" /></div>
              <div class="col-sm-2 mb-1">
                <b-button block variant="outline-primary" class="is-border-1" :disabled="!keyword || searching" @click="searchByNumber">
                  <b-spinner small v-if="searching" /> {{ $t('cdSearch') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="error" class="text-danger small mt-1">{{ error }}</div>
      </div>
    </div>

    <!-- Danh sách kết quả tìm theo số -->
    <div class="card mb-3" v-if="results.length">
      <div class="card-header py-2 small">{{ $t('cdResults') }} ({{ results.length }})</div>
      <div class="table-responsive">
        <table class="table table-sm table-hover mb-0">
          <thead><tr>
            <th>{{ $t('cdTime') }}</th><th>{{ $t('cdDirection') }}</th><th>{{ $t('cdFrom') }}</th><th>{{ $t('cdTo') }}</th>
            <th>{{ $t('cdDuration') }}</th><th>{{ $t('cdHangup') }}</th><th></th>
          </tr></thead>
          <tbody>
            <tr v-for="c in results" :key="c._id">
              <td class="small">{{ fmtTime(c.startTime) }}</td>
              <td><span class="badge" :class="c.direction === 'inbound' ? 'badge-info' : 'badge-secondary'">{{ c.direction }}</span></td>
              <td>{{ c.fromNumber }}</td><td>{{ c.toNumber }}</td>
              <td class="small">{{ c.billDuration || 0 }}s</td>
              <td class="small">{{ c.hangupCause || '—' }}</td>
              <td><b-button size="sm" variant="primary" :disabled="analyzing" @click="analyze(c._id)">{{ $t('cdDiagnose') }}</b-button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Báo cáo chẩn đoán -->
    <div class="card" v-if="analysis">
      <div class="card-body">
        <div v-if="analyzing" class="text-center py-4"><b-spinner /> <div class="small text-muted mt-2">{{ $t('cdAnalyzing') }}</div></div>
        <template v-else>
          <div class="d-flex align-items-center flex-wrap mb-2">
            <span class="badge badge-pill mr-2 px-3 py-2" :class="layerClass(analysis.layer)">
              {{ $t('cdLayer') }}: {{ layerLabel(analysis.layer) }}
            </span>
            <span class="badge badge-pill badge-light border mr-2 px-3 py-2">
              {{ $t('cdOwner') }}: {{ ownerLabel(analysis.faultOwner) }}
            </span>
            <span class="text-muted small">{{ $t('cdConfidence') }}: {{ Math.round((analysis.confidence || 0) * 100) }}%</span>
          </div>

          <div v-if="analysis.numberHealth" class="small mb-2">
            <span class="text-muted">{{ $t('cdNumber') }}:</span>
            <span class="font-weight-bold" v-if="analysis.numberHealth.number">{{ analysis.numberHealth.number }}</span>
            <span class="badge badge-light border ml-1">{{ numberTypeLabel(analysis.numberHealth.type) }}</span>
            <span v-for="(p, i) in (analysis.numberHealth.problems || [])" :key="i" class="badge badge-warning ml-1">{{ p.vi }}</span>
          </div>

          <div v-if="analysis.provider && (analysis.provider.network || analysis.provider.gateway)" class="small mb-2">
            <span class="text-muted">{{ $t('cdProvider') }}:</span>
            <span class="font-weight-bold">{{ analysis.provider.network || analysis.provider.gateway }}</span>
            <span v-if="analysis.provider.gateway && analysis.provider.network" class="text-muted">/ {{ analysis.provider.gateway }}</span>
            <span v-if="analysis.provider.gatewayProxy" class="text-muted">(<code>{{ analysis.provider.gatewayProxy }}</code><span v-if="analysis.provider.hopMatched"> ✓</span>)</span>
          </div>

          <div v-if="analysis.traceExpired" class="alert alert-warning py-2 small mb-2">{{ $t('cdTraceExpired') }}</div>
          <div v-else-if="analysis.correlation === 'none'" class="alert alert-secondary py-2 small mb-2">{{ noTraceText(analysis.homerError) }}</div>

          <p class="font-weight-bold mb-2">{{ analysis.summary }}</p>

          <!-- AI -->
          <div class="bg-light rounded p-3 mb-3" style="white-space: pre-wrap; font-size: 0.9rem;">{{ analysis.result }}</div>

          <!-- Bằng chứng -->
          <div v-if="analysis.evidence && analysis.evidence.length" class="mb-3">
            <div class="small text-muted mb-1">{{ $t('cdEvidence') }}</div>
            <span v-for="(e, i) in analysis.evidence" :key="i" class="badge badge-light border mr-1 mb-1">
              {{ e.field }}: {{ e.value }} <span class="text-muted">({{ e.source }})</span>
            </span>
          </div>

          <!-- Topology -->
          <div v-if="analysis.topology && analysis.topology.length" class="small mb-2">
            <span class="text-muted">{{ $t('cdTopology') }}:</span>
            <span v-for="(ip, i) in analysis.topology" :key="ip">
              <code>{{ ip }}</code><span v-if="i < analysis.topology.length - 1"> → </span>
            </span>
          </div>

          <!-- Chất lượng thoại đo từ RTCP (Homer) -->
          <div v-if="analysis.qos && analysis.qos.streams && analysis.qos.streams.length" class="mb-2">
            <div class="small text-muted mb-1">
              {{ $t('cdRtp') }}
              <span v-if="analysis.codecs && analysis.codecs.length">— codec: <code>{{ analysis.codecs.join(', ') }}</code></span>
            </div>
            <div class="table-responsive">
              <table class="table table-sm mb-0" style="font-size: 0.8rem;">
                <thead><tr><th>{{ $t('cdRtpStream') }}</th><th>{{ $t('cdRtpLoss') }}</th><th>{{ $t('cdRtpJitter') }}</th><th>{{ $t('cdRtpBad') }}</th></tr></thead>
                <tbody>
                  <tr v-for="(s, i) in analysis.qos.streams" :key="i">
                    <td><code>{{ s.sender }}</code> → <code>{{ s.receiver }}</code></td>
                    <td :class="{ 'text-danger font-weight-bold': s.bad }">{{ s.lossPctAvg }}% <span class="text-muted">({{ $t('cdRtpPeak') }} {{ s.lossPctMax }}%)</span></td>
                    <td :class="{ 'text-danger font-weight-bold': s.bad }">{{ s.jitterMsAvg }}ms <span class="text-muted">({{ $t('cdRtpPeak') }} {{ s.jitterMsMax }}ms)</span></td>
                    <td :class="{ 'text-warning font-weight-bold': s.bad }">{{ s.badSamples }}/{{ s.samples }} ({{ s.badPct }}%)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Call ladder -->
          <div v-if="analysis.ladder && analysis.ladder.length">
            <b-button size="sm" variant="link" class="px-0" @click="showLadder = !showLadder">
              {{ showLadder ? '▾' : '▸' }} {{ $t('cdLadder') }} ({{ analysis.ladder.length }})
            </b-button>
            <div v-if="showLadder" class="table-responsive">
              <table class="table table-sm mb-0" style="font-size: 0.8rem;">
                <thead><tr><th>{{ $t('cdTime') }}</th><th>Src</th><th>Dst</th><th>SIP</th></tr></thead>
                <tbody>
                  <tr v-for="(m, i) in analysis.ladder" :key="i">
                    <td>{{ fmtTimeMs(m.tsMs) }}</td><td><code>{{ m.src }}</code></td><td><code>{{ m.dst }}</code></td>
                    <td><span :class="sipClass(m.method)">{{ m.method }}</span> <span class="text-muted">{{ m.cseq }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'CallDiagnostic',
  data() {
    const today = new Date();
    const past = new Date(Date.now() - 30 * 24 * 3600 * 1000);
    const d = (x) => x.toISOString().slice(0, 10);
    return {
      callIdInput: '',
      keyword: '',
      fromDate: d(past),
      toDate: d(today),
      results: [],
      analysis: null,
      searching: false,
      analyzing: false,
      byId: false,
      showLadder: false,
      error: '',
    };
  },
  methods: {
    rangeMs() {
      const from = this.fromDate ? new Date(this.fromDate + 'T00:00:00').getTime() : null;
      const to = this.toDate ? new Date(this.toDate + 'T23:59:59').getTime() : null;
      return { from, to };
    },
    async searchByNumber() {
      if (!this.keyword) return;
      this.searching = true; this.error = ''; this.results = [];
      try {
        const { from, to } = this.rangeMs();
        const res = await this.$service.get('/diagnostic-call/lookup', { params: { keyword: this.keyword, from, to } });
        this.results = (res.data && res.data.docs) || [];
        if (!this.results.length) this.error = this.$t('cdEmpty');
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error) || this.$t('cdError');
      } finally { this.searching = false; }
    },
    async analyze(callId) {
      this.analyzing = true; this.error = ''; this.analysis = null; this.showLadder = false;
      try {
        const res = await this.$service.post(`/diagnostic-call/${callId}/analyze`);
        this.analysis = (res.data && res.data.doc) || null;
        if (!this.analysis) this.error = this.$t('cdError');
      } catch (e) {
        const code = e.response && e.response.data && e.response.data.error;
        this.error = code === 'OPENAI_KEY_MISSING' ? this.$t('cdNoKey')
          : code === 'CALL_NOT_FOUND' ? this.$t('cdNotFound')
          : this.$t('cdError');
      } finally { this.analyzing = false; this.byId = false; }
    },
    analyzeById() {
      if (!this.callIdInput) return;
      this.byId = true;
      this.analyze(this.callIdInput);
    },
    fmtTime(ms) { return ms ? new Date(ms).toLocaleString() : '—'; },
    fmtTimeMs(ms) { return ms ? new Date(ms).toLocaleTimeString() : '—'; },
    layerLabel(l) { return this.$t('layer_' + (l || 'none')); },
    ownerLabel(o) { return this.$t('owner_' + (o || 'none')); },
    numberTypeLabel(t) { return this.$t('numType_' + (t || 'normal')); },
    layerClass(l) {
      if (l === 'none' || !l) return 'badge-success';
      if (['carrier', 'sip', 'number'].includes(l)) return 'badge-danger';
      return 'badge-warning';
    },
    sipClass(m) {
      const code = parseInt(m, 10);
      if (!isNaN(code)) {
        if (code >= 200 && code < 300) return 'text-success font-weight-bold';
        if (code >= 400) return 'text-danger font-weight-bold';
        return 'text-muted';
      }
      return 'text-primary';
    },
    // Nói rõ vì sao không có trace SIP (sai URL/sai tài khoản khác hẳn với "Homer không có bản tin").
    noTraceText(err) {
      const key = { NOT_CONFIGURED: 'cdNoTraceNotConfigured', HOMER_NOT_CONFIGURED: 'cdNoTraceNotConfigured',
        HOMER_BAD_URL: 'cdNoTraceBadUrl', HOMER_AUTH_FAILED: 'cdNoTraceAuth',
        NO_CALL_ID: 'cdNoTraceNoCallId', NO_MESSAGES: 'cdNoTraceNoMessages' }[err];
      return key ? this.$t(key) : this.$t('cdNoTrace');
    },
  },
  i18n: {
    messages: {
      en: {
        cdTitle: 'Call diagnostic', cdSubtitle: 'Enter a Call ID or phone number + time to diagnose where the call problem is (correlates Callio + Homer SIP trace + AI).',
        diagBreadcrumb: 'Diagnostic', cdOr: 'or',
        cdCallId: 'Call ID (Call._id)', cdCallIdPh: 'e.g. 6a1d63f4433f174ae37c89a0', cdByNumber: 'Or search by phone number + time',
        cdNumberPh: 'Phone number', cdSearch: 'Search', cdDiagnose: 'Diagnose', cdResults: 'Results', cdAnalyzing: 'Analyzing call...',
        cdTime: 'Time', cdDirection: 'Direction', cdFrom: 'From', cdTo: 'To', cdDuration: 'Talk', cdHangup: 'Hangup cause',
        cdLayer: 'Layer', cdOwner: 'Responsible', cdConfidence: 'Confidence', cdEvidence: 'Evidence', cdTopology: 'Topology', cdLadder: 'Call ladder (Homer SIP)', cdProvider: 'Carrier/Route',
        cdTraceExpired: 'SIP trace older than retention (~10 days) — diagnosing from hangup cause only.',
        cdNoTrace: 'No SIP trace correlated (Homer unavailable or no match) — diagnosing from Callio data only.',
        cdNoTraceNotConfigured: 'Homer credentials are not set (admin > Etc: homer_url / homer_user / homer_pass) — diagnosing from Callio data only.',
        cdNoTraceBadUrl: 'Homer URL is wrong (server answered with a redirect) — fix homer_url in Etc. Diagnosing from Callio data only.',
        cdNoTraceAuth: 'Homer login failed (wrong user/password) — fix homer_user / homer_pass in Etc. Diagnosing from Callio data only.',
        cdNoTraceNoCallId: 'This call has no SIP Call-ID stored — cannot correlate with Homer.',
        cdNoTraceNoMessages: 'Homer has no SIP messages for this call (not captured or already purged) — diagnosing from Callio data only.',
        cdRtp: 'Voice quality (RTCP)', cdRtpStream: 'Stream', cdRtpLoss: 'Packet loss (avg)', cdRtpJitter: 'Jitter (avg)', cdRtpBad: 'Bad intervals', cdRtpPeak: 'peak',
        cdEmpty: 'No calls found', cdError: 'Something went wrong', cdNoKey: 'OpenAI key not configured', cdNotFound: 'Call not found',
        cdNumber: 'Number',
        numType_rental: 'Rented number', numType_byo: 'External (BYO)', numType_random: 'Random pool', numType_normal: 'Number',
        layer_device: 'Callee device/network', layer_agent: 'Agent (device/network)', layer_sip: 'SIP infra (Kamailio)', layer_freeswitch: 'PBX (media/codec)', layer_carrier: 'Carrier', layer_number: 'Calling number', layer_none: 'No fault',
        owner_callee: 'Callee', owner_caller: 'Caller/Agent', owner_agent_network: 'Agent network', owner_infra: 'Callio infra', owner_carrier: 'Carrier', owner_none: 'Nobody (normal)',
      },
      vi: {
        cdTitle: 'Chẩn đoán cuộc gọi', cdSubtitle: 'Nhập Call ID hoặc số điện thoại + thời gian để chẩn đoán lỗi cuộc gọi nằm ở tầng nào (đối chiếu Callio + SIP trace Homer + AI).',
        diagBreadcrumb: 'Chẩn đoán', cdOr: 'hoặc',
        cdCallId: 'Call ID (Call._id)', cdCallIdPh: 'vd 6a1d63f4433f174ae37c89a0', cdByNumber: 'Hoặc tìm theo số điện thoại + thời gian',
        cdNumberPh: 'Số điện thoại', cdSearch: 'Tìm', cdDiagnose: 'Chẩn đoán', cdResults: 'Kết quả', cdAnalyzing: 'Đang phân tích cuộc gọi...',
        cdTime: 'Thời gian', cdDirection: 'Hướng', cdFrom: 'Gọi từ', cdTo: 'Gọi đến', cdDuration: 'Đàm thoại', cdHangup: 'Lý do gác máy',
        cdLayer: 'Tầng lỗi', cdOwner: 'Ai chịu', cdConfidence: 'Độ tin cậy', cdEvidence: 'Bằng chứng', cdTopology: 'Đường đi', cdLadder: 'Call ladder (SIP Homer)', cdProvider: 'Nhà mạng/Tuyến',
        cdTraceExpired: 'Trace SIP đã quá hạn lưu (~10 ngày) — chỉ chẩn đoán theo lý do gác máy.',
        cdNoTrace: 'Không đối chiếu được trace SIP (Homer không sẵn sàng hoặc không khớp) — chỉ chẩn đoán theo dữ liệu Callio.',
        cdNoTraceNotConfigured: 'Chưa khai báo tài khoản Homer (admin > Cấu hình: homer_url / homer_user / homer_pass) — chỉ chẩn đoán theo dữ liệu Callio.',
        cdNoTraceBadUrl: 'Địa chỉ Homer sai (máy chủ trả về chuyển hướng) — sửa lại homer_url trong Cấu hình. Tạm thời chỉ chẩn đoán theo dữ liệu Callio.',
        cdNoTraceAuth: 'Đăng nhập Homer thất bại (sai tài khoản/mật khẩu) — sửa homer_user / homer_pass trong Cấu hình. Tạm thời chỉ chẩn đoán theo dữ liệu Callio.',
        cdNoTraceNoCallId: 'Cuộc gọi này không lưu Call-ID SIP — không đối chiếu được với Homer.',
        cdNoTraceNoMessages: 'Homer không có bản tin SIP của cuộc gọi này (không được capture hoặc đã hết hạn lưu) — chỉ chẩn đoán theo dữ liệu Callio.',
        cdRtp: 'Chất lượng thoại (RTCP)', cdRtpStream: 'Luồng', cdRtpLoss: 'Mất gói (TB)', cdRtpJitter: 'Jitter (TB)', cdRtpBad: 'Kỳ xấu', cdRtpPeak: 'đỉnh',
        cdEmpty: 'Không tìm thấy cuộc gọi', cdError: 'Có lỗi xảy ra', cdNoKey: 'Chưa cấu hình OpenAI key', cdNotFound: 'Không tìm thấy cuộc gọi',
        cdNumber: 'Số gọi đi',
        numType_rental: 'Số cho thuê', numType_byo: 'Số ngoài của khách', numType_random: 'Số random (pool)', numType_normal: 'Số',
        layer_device: 'Thiết bị/mạng đầu kia', layer_agent: 'Nhân viên (máy/mạng)', layer_sip: 'Hạ tầng SIP (Kamailio)', layer_freeswitch: 'Tổng đài (media/codec)', layer_carrier: 'Nhà mạng', layer_number: 'Số gọi đi', layer_none: 'Không lỗi',
        owner_callee: 'Khách (đầu kia)', owner_caller: 'Người gọi/nhân viên', owner_agent_network: 'Mạng nhân viên', owner_infra: 'Hạ tầng Callio', owner_carrier: 'Nhà mạng', owner_none: 'Không ai (bình thường)',
      },
    },
  },
};
</script>
